// Adapted from Cloudflare OS packages/mcp-shared/src/{endpoint,fetch}.ts, Apache 2.0.

// Hosts a connection request must never reach, on the first request or any redirect hop.
const BLOCKED_HOST_PATTERNS = [
  /^localhost$/i,
  /\.localhost$/i,
  /^127\./,
  /^0\./,
  /^10\./,
  /^192\.168\./,
  /^172\.(1[6-9]|2[0-9]|3[01])\./,
  /^169\.254\./,
  /^\[?::1\]?$/,
  /^\[?f[cd][0-9a-f]{2}:/i,
  /^metadata\./i,
  /\.internal$/i,
];

// Rewrites integer and IPv4-mapped IPv6 spellings of a host into dotted-quad form, and strips a
// trailing DNS root dot so it cannot dodge an end-anchored pattern below (e.g. "localhost.").
function normalizeHost(hostname: string): string {
  const host = hostname.replace(/\.+$/, '');

  const mapped = /^\[::ffff:([0-9a-f]{1,4}):([0-9a-f]{1,4})\]$/i.exec(host);
  if (mapped) {
    const high = Number.parseInt(mapped[1] ?? '0', 16);
    const low = Number.parseInt(mapped[2] ?? '0', 16);
    return [high >>> 8, high & 0xff, low >>> 8, low & 0xff].join('.');
  }

  let asInteger = Number.NaN;
  if (/^0[xX][0-9a-fA-F]+$/.test(host)) asInteger = Number(host);
  else if (/^0[0-7]*$/.test(host)) asInteger = Number.parseInt(host, 8);
  else if (/^[1-9][0-9]*$/.test(host)) asInteger = Number(host);

  if (Number.isInteger(asInteger) && asInteger >= 0 && asInteger <= 0xffffffff) {
    return [24, 16, 8, 0].map((shift) => (asInteger >>> shift) & 0xff).join('.');
  }
  return host;
}

/**
 * Whether a host must never be fetched, on the initial request or any redirect hop.
 * Pass a `URL.hostname`; a raw string skips the normalization `new URL()` would apply.
 */
// An IPv6 literal is public only inside global unicast (2000::/3): link-local, unique-local, NAT64 and the rest are not.
const IPV6_LITERAL = /^\[?([0-9a-f]*:[0-9a-f:.]*)\]?$/i;
const GLOBAL_UNICAST = /^[23][0-9a-f]{3}:/i;

export function isBlockedHost(hostname: string): boolean {
  const host = normalizeHost(hostname);
  const v6 = IPV6_LITERAL.exec(host)?.[1];
  if (v6 !== undefined && !GLOBAL_UNICAST.test(v6)) return true;
  return BLOCKED_HOST_PATTERNS.some((pattern) => pattern.test(host));
}

function isAllowedUrl(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  return parsed.protocol === 'https:' && !isBlockedHost(parsed.hostname);
}

function hostForMessage(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return 'that address';
  }
}

/** Thrown when a request is refused before the platform fetch() ever runs. */
export class EgressNotStartedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EgressNotStartedError';
  }
}

// Redirect hops permitted before a chain is treated as a loop.
const MAX_REDIRECTS = 3;
const REDIRECT_STATUSES = new Set([301, 302, 303, 307, 308]);
const DEFAULT_TIMEOUT_MS = 30_000;

// Every credential header must earn a place here; anything else is dropped cross-origin.
const CROSS_ORIGIN_HEADER_ALLOWLIST = ['Accept', 'Content-Type', 'User-Agent'];

/** Deadline options for one guarded outbound operation. */
export type EgressOptions = {
  /** Bounds the whole operation, including redirects. Defaults to 30 seconds. */
  timeoutMs?: number;
  /** Absolute deadline in epoch ms, shared across multiple guarded calls. */
  deadline?: number;
};

/**
 * Fetches `url`, following redirects manually so every hop is revalidated.
 * Refuses the first URL by throwing; refuses a redirect by returning it.
 */
export async function guardedFetch(
  url: string,
  init: RequestInit,
  options: EgressOptions = {},
): Promise<Response> {
  if (!isAllowedUrl(url)) {
    throw new EgressNotStartedError(`Refusing to contact ${hostForMessage(url)}.`);
  }

  let current = url;
  let headers = new Headers(init.headers);
  let method = init.method ?? 'GET';
  let body = init.body;
  const origin = new URL(url).origin;

  const remaining =
    options.deadline === undefined
      ? (options.timeoutMs ?? DEFAULT_TIMEOUT_MS)
      : Math.max(0, options.deadline - Date.now());
  if (remaining === 0) throw new EgressNotStartedError('The outbound operation timed out.');
  const timeout = AbortSignal.timeout(remaining);
  const signal = init.signal ? AbortSignal.any([init.signal, timeout]) : timeout;

  for (let hop = 0; ; hop++) {
    const response = await fetch(current, {
      ...init,
      method,
      body,
      headers,
      redirect: 'manual',
      signal,
    });
    if (!REDIRECT_STATUSES.has(response.status)) return response;

    const location = response.headers.get('Location');
    if (!location || hop >= MAX_REDIRECTS) return response;

    const next = new URL(location, current).toString();
    if (!isAllowedUrl(next)) return response;

    const crossOrigin = new URL(next).origin !== origin;
    const becomesGet =
      response.status === 303 || (response.status < 307 && method !== 'GET' && method !== 'HEAD');

    // 307 and 308 preserve the request method and body across the redirect.
    if (crossOrigin && body != null && !becomesGet) return response;

    if (crossOrigin) {
      const kept = new Headers();
      for (const name of CROSS_ORIGIN_HEADER_ALLOWLIST) {
        const value = headers.get(name);
        if (value !== null) kept.set(name, value);
      }
      headers = kept;
    }

    if (becomesGet) {
      method = 'GET';
      body = undefined;
      headers = new Headers(headers);
      headers.delete('Content-Type');
      headers.delete('Content-Length');
    }
    await response.body?.cancel().catch(() => undefined);
    current = next;
  }
}

/** Cap on any single response body this gatekeeper buffers into memory. */
export const MAX_RESPONSE_BYTES = 1024 * 1024;

/** Reads a response body as text, refusing rather than truncating one over `maxBytes`. */
export async function readTextCapped(
  response: Response,
  maxBytes: number = MAX_RESPONSE_BYTES,
): Promise<string> {
  if (!response.body) return '';

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      total += value.byteLength;
      if (total > maxBytes) {
        await reader.cancel();
        throw new Error(`The server's response exceeded ${maxBytes} bytes.`);
      }
      chunks.push(value);
    }
  } finally {
    reader.releaseLock();
  }

  const joined = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    joined.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return new TextDecoder().decode(joined);
}
